import { z } from 'zod';
import { PLAY_EVENT_BENEFITS } from '../types/playEvent';
import { optionalE164PhoneSchema } from './phone';

// ─── Households ────────────────────────────────────────────────────────────

export const memberHouseholdCreateSchema = z.object({
  name: z
    .union([z.string(), z.null(), z.literal('')])
    .optional()
    .transform((value) => {
      if (value === undefined || value === null) return null;
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }),
});

export const memberHouseholdUpdateSchema = memberHouseholdCreateSchema;

export type MemberHouseholdCreateInput = z.infer<typeof memberHouseholdCreateSchema>;
export type MemberHouseholdUpdateInput = z.infer<typeof memberHouseholdUpdateSchema>;

// ─── Members ───────────────────────────────────────────────────────────────

const memberGenderSchema = z.enum(['male', 'female', 'other']);

const nullableEmailSchema = z
  .union([z.string().trim().email('Enter a valid email'), z.literal(''), z.null()])
  .optional()
  .transform((value): string | null => (value ? value : null));

export const memberCreateSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: nullableEmailSchema,
  phone: optionalE164PhoneSchema,
  dateOfBirth: z.coerce.date().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  studentId: z.string().uuid().nullable().optional(),
  householdId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().default(true),
  gender: memberGenderSchema,
});

export const memberUpdateSchema = memberCreateSchema.partial().extend({
  gender: memberGenderSchema.nullable().optional(),
});

export type MemberCreateInput = z.infer<typeof memberCreateSchema>;
export type MemberUpdateInput = z.infer<typeof memberUpdateSchema>;

/** Operator drawer form (raw strings for date/select inputs) — mirrors the membershipType drawer pattern. */
export const memberDrawerFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: z.string().optional(),
  phone: optionalE164PhoneSchema,
  /** `YYYY-MM-DD` from an HTML date input, or empty when unknown. */
  dateOfBirth: z.string().optional(),
  /** Empty string = no household. */
  householdId: z.string().optional(),
  isActive: z.boolean().default(true),
  /** Empty string = unset (allowed on edit). */
  gender: z.union([memberGenderSchema, z.literal('')]).default(''),
});

export type MemberDrawerFormValues = z.infer<typeof memberDrawerFormSchema>;

function memberFormCommonPayloadFields(values: MemberDrawerFormValues) {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email?.trim() ? values.email.trim() : null,
    phone: values.phone?.trim() ? values.phone.trim() : undefined,
    dateOfBirth: values.dateOfBirth?.trim() ? new Date(values.dateOfBirth) : null,
    householdId: values.householdId?.trim() ? values.householdId.trim() : null,
    isActive: values.isActive,
    gender: values.gender?.trim() ? values.gender : null,
  };
}

export function memberFormToCreatePayload(values: MemberDrawerFormValues): MemberCreateInput {
  return memberCreateSchema.parse(memberFormCommonPayloadFields(values));
}

export function memberFormToUpdatePayload(values: MemberDrawerFormValues): MemberUpdateInput {
  return memberUpdateSchema.parse(memberFormCommonPayloadFields(values));
}

// ─── Membership add-ons ────────────────────────────────────────────────────

const addonDescriptionSchema = z
  .union([z.string(), z.null(), z.literal('')])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null) return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  });

export const membershipAddonPricingModeSchema = z.enum(['per_adult', 'per_membership']);

export const membershipAddonCreateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  description: addonDescriptionSchema,
  priceCents: z.number().int().min(0, 'Price cannot be negative'),
  concessionPriceCents: z.number().int().min(0, 'Concession price cannot be negative').nullable().optional(),
  currency: z
    .string()
    .length(3, 'Currency must be 3 characters')
    .transform((value) => value.toUpperCase()),
  pricingMode: membershipAddonPricingModeSchema,
  sortOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  playEventBenefit: z.enum(PLAY_EVENT_BENEFITS).default('none'),
  playEventDiscountPercent: z.number().int().min(0).max(100).nullable().optional(),
});

export const membershipAddonUpdateSchema = membershipAddonCreateSchema.partial();

export type MembershipAddonCreateInput = z.infer<typeof membershipAddonCreateSchema>;
export type MembershipAddonUpdateInput = z.infer<typeof membershipAddonUpdateSchema>;

/** Operator drawer form (dollars, not cents) — mirrors the membershipType drawer form pattern. */
export const membershipAddonDrawerFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(255, 'Name too long'),
    description: z.string().optional(),
    price: z.coerce.number().min(0, 'Price cannot be negative'),
    hasConcessionPrice: z.boolean().default(false),
    concessionPrice: z.coerce.number().min(0, 'Concession price cannot be negative').nullable().optional(),
    pricingMode: membershipAddonPricingModeSchema,
    isActive: z.boolean().default(true),
    playEventBenefit: z.enum(PLAY_EVENT_BENEFITS).default('none'),
    playEventDiscountPercent: z.coerce
      .number()
      .min(0, 'Discount must be 0–100')
      .max(100, 'Discount must be 0–100')
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.playEventBenefit === 'discount_percent' && data.playEventDiscountPercent == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Discount percent is required when play benefit is a percentage discount',
        path: ['playEventDiscountPercent'],
      });
    }
  });

export type MembershipAddonDrawerFormValues = z.infer<typeof membershipAddonDrawerFormSchema>;

function drawerAddonCommonPayloadFields(values: MembershipAddonDrawerFormValues) {
  return {
    name: values.name,
    description: values.description,
    pricingMode: values.pricingMode,
    isActive: values.isActive,
    playEventBenefit: values.playEventBenefit,
    playEventDiscountPercent:
      values.playEventBenefit === 'discount_percent' ? values.playEventDiscountPercent ?? null : null,
  };
}

export function membershipAddonFormToCreatePayload(
  values: MembershipAddonDrawerFormValues,
  currency: string
): MembershipAddonCreateInput {
  return membershipAddonCreateSchema.parse({
    ...drawerAddonCommonPayloadFields(values),
    priceCents: Math.round(values.price * 100),
    concessionPriceCents:
      values.hasConcessionPrice && values.concessionPrice != null
        ? Math.round(values.concessionPrice * 100)
        : null,
    currency,
  });
}

export function membershipAddonFormToUpdatePayload(
  values: MembershipAddonDrawerFormValues,
  currency: string
): MembershipAddonUpdateInput {
  return membershipAddonUpdateSchema.parse({
    ...drawerAddonCommonPayloadFields(values),
    priceCents: Math.round(values.price * 100),
    concessionPriceCents:
      values.hasConcessionPrice && values.concessionPrice != null
        ? Math.round(values.concessionPrice * 100)
        : null,
    currency,
  });
}

// ─── Seat & add-on selections (quote / purchase / assign) ─────────────────

/** One covered seat: either an existing member, or details to create a new one (e.g. a spouse or child). */
export const membershipSeatMemberInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('existing'),
    memberId: z.string().uuid('Invalid member id'),
  }),
  z.object({
    kind: z.literal('new'),
    firstName: z.string().trim().min(1, 'First name is required').max(100),
    lastName: z.string().trim().min(1, 'Last name is required').max(100),
    email: nullableEmailSchema,
    phone: optionalE164PhoneSchema,
    dateOfBirth: z.coerce.date().nullable().optional(),
  }),
]);

export const membershipAddonSelectionInputSchema = z.object({
  addonId: z.string().uuid('Invalid add-on id'),
  /** Ties a per-adult add-on to a specific covered seat; omit for per-membership add-ons. */
  memberId: z.string().uuid().nullable().optional(),
  /** Defaults to the number of adult seats (per_adult) or 1 (per_membership) when omitted. */
  quantity: z.number().int().positive('Quantity must be a positive number').optional(),
});

export const membershipQuoteRequestSchema = z.object({
  membershipTypeId: z.string().uuid('Invalid membership type id'),
  seats: z.array(membershipSeatMemberInputSchema).min(1, 'At least one person must be selected'),
  addonSelections: z.array(membershipAddonSelectionInputSchema).default([]),
});

export type MembershipQuoteRequestInput = z.infer<typeof membershipQuoteRequestSchema>;

/** Same shape as the quote; used for both online Stripe purchase and staff assign-unpaid. */
export const membershipPurchaseRequestSchema = membershipQuoteRequestSchema.extend({
  householdId: z.string().uuid().nullable().optional(),
  /** Required acknowledgment when the club has surcharging enabled and a surcharge applies. */
  acknowledgeSurcharge: z.boolean().optional(),
});

export const membershipAssignRequestSchema = membershipPurchaseRequestSchema;

export type MembershipPurchaseRequestInput = z.infer<typeof membershipPurchaseRequestSchema>;
export type MembershipAssignRequestInput = z.infer<typeof membershipAssignRequestSchema>;

// ─── Payment recording, verification, renew, cancel ────────────────────────

export const membershipOfflinePaymentMethodSchema = z.enum(['cash', 'eftpos', 'bank_transfer', 'other']);

export const membershipOfflinePaymentRequestSchema = z.object({
  membershipId: z.string().uuid('Invalid membership id'),
  paymentMethod: membershipOfflinePaymentMethodSchema,
  note: z.string().trim().max(500).optional(),
});

export type MembershipOfflinePaymentRequestInput = z.infer<typeof membershipOfflinePaymentRequestSchema>;

export const membershipProofVerificationRequestSchema = z.object({
  membershipId: z.string().uuid('Invalid membership id'),
  approve: z.boolean(),
  note: z.string().trim().max(500).optional(),
});

export type MembershipProofVerificationRequestInput = z.infer<typeof membershipProofVerificationRequestSchema>;

export const membershipRenewRequestSchema = z.object({
  membershipId: z.string().uuid('Invalid membership id'),
  addonSelections: z.array(membershipAddonSelectionInputSchema).optional(),
});

export type MembershipRenewRequestInput = z.infer<typeof membershipRenewRequestSchema>;

export const membershipCancelRequestSchema = z.object({
  membershipId: z.string().uuid('Invalid membership id'),
  reason: z.string().trim().max(500).optional(),
  refund: z.boolean().default(false),
});

export type MembershipCancelRequestInput = z.infer<typeof membershipCancelRequestSchema>;

// ─── Public self-service join (no member directory access) ────────────────

/** Profile details for the authenticated primary member on the household step. */
export const publicMembershipPrimaryMemberInputSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  gender: memberGenderSchema,
  dateOfBirth: z.coerce.date().nullable().optional(),
  phone: optionalE164PhoneSchema,
});

export type PublicMembershipPrimaryMemberInput = z.infer<typeof publicMembershipPrimaryMemberInputSchema>;

/** An extra covered person (spouse/child) added by the joining member. */
export const publicMembershipJoinSeatInputSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  dateOfBirth: z.coerce.date().nullable().optional(),
  /** When set, the join flow auto-links to an existing active member at this club with the same email. */
  email: nullableEmailSchema,
});

/**
 * The primary seat is always the authenticated caller (resolved server-side from their account).
 * Optional `primaryMember` updates their club member profile before quote/join (household step).
 */
export const publicMembershipQuoteRequestSchema = z.object({
  membershipTypeId: z.string().uuid('Invalid membership type id'),
  primaryMember: publicMembershipPrimaryMemberInputSchema.optional(),
  additionalSeats: z.array(publicMembershipJoinSeatInputSchema).max(20).default([]),
  addonSelections: z.array(membershipAddonSelectionInputSchema).default([]),
});

export type PublicMembershipQuoteRequestInput = z.infer<typeof publicMembershipQuoteRequestSchema>;

export const publicMembershipJoinRequestSchema = publicMembershipQuoteRequestSchema;

export type PublicMembershipJoinRequestInput = z.infer<typeof publicMembershipJoinRequestSchema>;
